import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { Attendance, ClassRoom, Student, TimetableSlot } from '@/lib/types';
import { DAY_NAMES, formatTime } from '@/lib/constants';
import { useProfile } from '@/hooks/useProfile';
import { useTeachers } from '@/hooks/useTeachers';
import MakeupScheduleModal from './components/MakeupScheduleModal';
import MakeupCompletedModal from './components/MakeupCompletedModal';
import {
  buildClassInfoMaps,
  attendanceClassInfo,
  absenceReasonLabel,
  classInfoLine,
  type ClassInfoMaps,
} from './utils';
import {
  printAbsenceCalendar,
  type AbsencePrintRow,
  type AbsencePrintStatus,
} from './printAbsenceCalendar';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shortDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}`;
}

function displayDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${y}.${m}.${d} (${DAY_NAMES[date.getDay()]})`;
}

export default function AbsenceCalendarPage() {
  // viewDate 는 항상 해당 월의 1일로 유지
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [records, setRecords] = useState<Attendance[]>([]);
  const [unscheduled, setUnscheduled] = useState<Attendance[]>([]);
  const [notNeeded, setNotNeeded] = useState<Attendance[]>([]);
  const [studentMap, setStudentMap] = useState<Record<string, Student>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<Attendance | null>(null);
  const [completedTarget, setCompletedTarget] = useState<Attendance | null>(null);
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [showNotNeeded, setShowNotNeeded] = useState(false);
  const [classMaps, setClassMaps] = useState<ClassInfoMaps>({ classMap: {}, slotMap: {} });

  const { profile, loading: profileLoading } = useProfile();
  const { teachers } = useTeachers();
  const [teacherFilter, setTeacherFilter] = useState<string>('all');

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const teacherMap = useMemo(
    () => new Map(teachers.map((t) => [t.id, t.full_name || '이름 없음'])),
    [teachers]
  );

  // 담당 선생님이 있는 반들을 모아 필터 목록을 만들어요. 담당이 없는 반도 따로 볼 수 있게 해요.
  const teacherChips = useMemo(() => {
    const ids = new Set<string>();
    let hasNone = false;
    Object.values(classMaps.classMap).forEach((c) => {
      if (c.teacher_id) ids.add(c.teacher_id);
      else hasNone = true;
    });
    const list = Array.from(ids).map((id) => ({ id, name: teacherMap.get(id) || '이름 없음' }));
    list.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    return { list, hasNone };
  }, [classMaps, teacherMap]);

  const matchesTeacher = useCallback(
    (r: Attendance) => {
      if (teacherFilter === 'all') return true;
      const tid = classMaps.classMap[r.class_id]?.teacher_id || null;
      if (teacherFilter === 'none') return !tid;
      return tid === teacherFilter;
    },
    [teacherFilter, classMaps]
  );

  const loadData = useCallback(async () => {
    const academyId = profile?.academy_id;
    if (!academyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const mm = String(month + 1).padStart(2, '0');
      const monthStart = `${year}-${mm}-01`;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const monthEnd = `${year}-${mm}-${String(daysInMonth).padStart(2, '0')}`;

      const [attRes, unscheduledRes, notNeededRes, studentsRes, classesRes, slotsRes] = await Promise.all([
        supabase
          .from('attendance')
          .select('*')
          .in('status', ['absent', 'makeup'])
          .gte('date', monthStart)
          .lte('date', monthEnd),
        supabase
          .from('attendance')
          .select('*')
          .eq('status', 'absent')
          .is('makeup_date', null)
          .or('makeup_status.is.null,makeup_status.eq.pending')
          .gte('date', monthStart)
          .lte('date', monthEnd)
          .order('date', { ascending: false }),
        supabase
          .from('attendance')
          .select('*')
          .eq('status', 'absent')
          .eq('makeup_status', 'not_needed')
          .gte('date', monthStart)
          .lte('date', monthEnd)
          .order('date', { ascending: false }),
        supabase.rpc('get_students_by_academy', { p_academy_id: academyId }),
        supabase.from('classes').select('*').eq('academy_id', academyId),
        supabase.from('timetable').select('*'),
      ]);

      if (attRes.error) throw attRes.error;
      if (unscheduledRes.error) throw unscheduledRes.error;
      if (notNeededRes.error) throw notNeededRes.error;
      if (studentsRes.error) throw studentsRes.error;

      const sm: Record<string, Student> = {};
      ((studentsRes.data as Student[]) || []).forEach((s) => {
        sm[s.id] = s;
      });

      // 수업명·요일별 수업 시간을 찾을 수 있게 인덱스를 만들고, 해당 학원 수업의 시간표만 남겨요.
      const classList = (classesRes.data as ClassRoom[]) || [];
      const classIds = new Set(classList.map((c) => c.id));
      const slotList = ((slotsRes.data as TimetableSlot[]) || []).filter((s) =>
        classIds.has(s.class_id)
      );

      setRecords((attRes.data as Attendance[]) || []);
      setUnscheduled((unscheduledRes.data as Attendance[]) || []);
      setNotNeeded((notNeededRes.data as Attendance[]) || []);
      setStudentMap(sm);
      setClassMaps(buildClassInfoMaps(classList, slotList));
    } catch (err) {
      setError('결석 정보를 불러오지 못했어요.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year, month, profile]);

  useEffect(() => {
    if (!profileLoading && profile?.academy_id) loadData();
  }, [loadData, profileLoading, profile]);

  function changeMonth(amount: number) {
    setViewDate(new Date(year, month + amount, 1));
  }

  // 완료된 보충은 일정을 잡는 팝업 대신, 언제 완료했는지 보여주는 팝업을 띄워요.
  function handleRecordClick(record: Attendance) {
    if (record.makeup_status === 'completed') {
      setCompletedTarget(record);
    } else {
      setScheduleTarget(record);
    }
  }

  async function scheduleMakeup(date: string, time: string) {
    if (!scheduleTarget) return;
    setSaving(true);
    try {
      const wasCompleted = scheduleTarget.makeup_status === 'completed';
      const { error: err } = await supabase
        .from('attendance')
        .update({
          makeup_date: date,
          makeup_time: time || null,
          makeup_status: wasCompleted ? 'completed' : 'pending',
          // 이미 완료된 보충이면 완료일도 함께 갱신해서 상태가 어긋나지 않게 해요
          makeup_completed_date: wasCompleted ? date : scheduleTarget.makeup_completed_date,
        })
        .eq('id', scheduleTarget.id);
      if (err) throw err;
      setScheduleTarget(null);
      await loadData();
    } catch (err) {
      setError('보충 일정 저장에 실패했어요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // 보충이 필요 없는 결석으로 표시 (보충 미정 목록에서 빠져요)
  async function markNotNeeded(record: Attendance) {
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('attendance')
        .update({
          makeup_status: 'not_needed',
          makeup_date: null,
          makeup_time: null,
          makeup_completed_date: null,
        })
        .eq('id', record.id);
      if (err) throw err;
      setScheduleTarget(null);
      await loadData();
    } catch (err) {
      setError('보충 불필요 처리에 실패했어요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // 다시 보충이 필요한 결석으로 되돌리기
  async function restoreNeeded(record: Attendance) {
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('attendance')
        .update({ makeup_status: 'pending' })
        .eq('id', record.id);
      if (err) throw err;
      setScheduleTarget(null);
      await loadData();
    } catch (err) {
      setError('보충 필요 처리에 실패했어요.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // 선택한 선생님에 해당하는 기록만 걸러요.
  const filteredRecords = useMemo(
    () => records.filter(matchesTeacher),
    [records, matchesTeacher]
  );
  const filteredUnscheduled = useMemo(
    () => unscheduled.filter(matchesTeacher),
    [unscheduled, matchesTeacher]
  );
  const filteredNotNeeded = useMemo(
    () => notNeeded.filter(matchesTeacher),
    [notNeeded, matchesTeacher]
  );

  const byDate = useMemo(() => {
    const map: Record<string, Attendance[]> = {};
    filteredRecords.forEach((r) => {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    });
    return map;
  }, [filteredRecords]);

  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(d);
    return result;
  }, [year, month]);

  const todayStr = toDateStr(new Date());

  const scheduledCount = filteredRecords.filter(
    (r) => r.makeup_status !== 'completed' && r.makeup_status !== 'not_needed' && !!r.makeup_date
  ).length;
  const completedCount = filteredRecords.filter((r) => r.makeup_status === 'completed').length;
  const unscheduledInMonth = filteredRecords.filter(
    (r) => r.makeup_status !== 'completed' && r.makeup_status !== 'not_needed' && !r.makeup_date
  ).length;
  const notNeededInMonth = filteredRecords.filter((r) => r.makeup_status === 'not_needed').length;

  const activeTeacherName =
    teacherFilter === 'all'
      ? '전체 선생님'
      : teacherFilter === 'none'
        ? '담당 미지정'
        : teacherMap.get(teacherFilter) || '선생님';

  // 인쇄용 행을 만들어요. 현재 선생님 필터가 적용된 기록만 대상이에요.
  const printRows: AbsencePrintRow[] = useMemo(
    () =>
      filteredRecords.map((r) => {
        const completed = r.makeup_status === 'completed';
        const notNeeded = r.makeup_status === 'not_needed';
        const scheduled = !completed && !notNeeded && !!r.makeup_date;
        const student = studentMap[r.student_id];
        const doneDate = completed ? r.makeup_completed_date || r.makeup_date : null;
        const status: AbsencePrintStatus = notNeeded
          ? 'notNeeded'
          : completed
            ? 'completed'
            : scheduled
              ? 'scheduled'
              : 'unscheduled';
        const statusText = notNeeded
          ? '보충 불필요'
          : completed
            ? '보충 완료'
            : scheduled
              ? '보충 예정'
              : '보충 미정';
        return {
          date: r.date,
          studentName: student?.name || '삭제된 학생',
          grade: student?.grade || null,
          className: attendanceClassInfo(r, classMaps).className,
          reason: absenceReasonLabel(r),
          status,
          statusText,
          makeupDate: r.makeup_date,
          makeupTime: r.makeup_time,
          completedDate: doneDate,
        };
      }),
    [filteredRecords, studentMap, classMaps]
  );

  function handlePrint() {
    printAbsenceCalendar({
      title: '결석보충 내역',
      subtitle:
        teacherFilter === 'all'
          ? '전체 선생님의 결석·보충 기록'
          : teacherFilter === 'none'
            ? '담당 미지정 수업의 결석·보충 기록'
            : `${activeTeacherName} 선생님 담당 수업의 결석·보충 기록`,
      monthLabel: `${year}년 ${month + 1}월`,
      teacherName: activeTeacherName,
      summary: {
        total: filteredRecords.length,
        scheduled: scheduledCount,
        completed: completedCount,
        unscheduled: unscheduledInMonth,
        notNeeded: notNeededInMonth,
      },
      rows: printRows,
    });
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">결석보충달력</h2>
          <p className="mt-1 text-sm text-foreground-500">
            결석한 학생만 월별 달력에 표시돼요. 보충을 잡으면 일정이, 마치면 완료가 함께 나와요.
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-primary-600">
            <i className="ri-cursor-line"></i>
            달력 칸의 학생 이름을 클릭하면 바로 보충 일정을 잡거나 수정할 수 있어요.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            disabled={loading}
            title={`${activeTeacherName}의 ${year}년 ${month + 1}월 결석보충 내역을 인쇄해요`}
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-printer-line"></i>
            인쇄
          </button>
          <button
            onClick={() => changeMonth(-1)}
            title="이전 달"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="min-w-[120px] text-center text-base font-bold text-foreground-950">
            {year}년 {month + 1}월
          </span>
          <button
            onClick={() => changeMonth(1)}
            title="다음 달"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      {/* 선생님별 필터 */}
      {(teacherChips.list.length > 0 || teacherChips.hasNone) && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setTeacherFilter('all')}
            className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
              teacherFilter === 'all'
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            <i className="ri-group-line text-xs"></i>
            전체
          </button>
          {teacherChips.list.map((t) => {
            const active = teacherFilter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTeacherFilter(t.id)}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className="ri-user-line text-xs"></i>
                {t.name}
              </button>
            );
          })}
          {teacherChips.hasNone && (
            <button
              onClick={() => setTeacherFilter('none')}
              className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                teacherFilter === 'none'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className="ri-user-unfollow-line text-xs"></i>
              담당 미지정
            </button>
          )}
        </div>
      )}

      {/* 요약 */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
        <i className="ri-filter-3-line text-secondary-500"></i>
        {teacherFilter === 'all' ? (
          <span>모든 선생님의 결석·보충 기록을 함께 보여줘요.</span>
        ) : (
          <span>
            <strong className="font-semibold text-foreground-900">{activeTeacherName}</strong>
            {teacherFilter === 'none' ? ' 수업' : ' 선생님'}의 결석·보충 기록만 보여줘요.
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">이번 달 결석</p>
          <p className="mt-1 text-2xl font-bold text-foreground-950">{filteredRecords.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">보충 예정</p>
          <p className="mt-1 text-2xl font-bold text-secondary-600">{scheduledCount}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">보충 완료</p>
          <p className="mt-1 text-2xl font-bold text-primary-600">{completedCount}</p>
        </div>
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
          <p className="text-sm text-accent-700">보충 미정 (이번 달)</p>
          <p className="mt-1 text-2xl font-bold text-accent-700">{unscheduledInMonth}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-white p-4">
          <p className="text-sm text-foreground-500">보충 불필요</p>
          <p className="mt-1 text-2xl font-bold text-foreground-500">{notNeededInMonth}</p>
        </div>
      </div>

      {/* 범례 */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-600">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span> 보충 미정 (아직 안 잡음)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-secondary-400"></span> 보충 예정
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span> 보충 완료
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground-300"></span> 보충 불필요
        </span>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 border-b border-background-200 bg-background-50">
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-xs font-semibold text-foreground-500"
              >
                {d}
              </div>
            ))}
          </div>

          {/* 달력 그리드 */}
          <div className="grid grid-cols-7">
            {cells.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`blank-${idx}`}
                    className="min-h-[96px] border-r border-b border-background-100 last:border-r-0"
                  ></div>
                );
              }
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayRecords = byDate[dateStr] || [];
              const isToday = dateStr === todayStr;
              return (
                <div
                  key={day}
                  className="min-h-[96px] border-r border-b border-background-100 p-1.5 last:border-r-0"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                        isToday ? 'bg-primary-500 text-background-50' : 'text-foreground-700'
                      }`}
                    >
                      {day}
                    </span>
                  </div>

                  <div className="mt-1 space-y-1">
                    {dayRecords.map((r) => {
                      const completed = r.makeup_status === 'completed';
                      const notNeeded = r.makeup_status === 'not_needed';
                      const scheduled = !completed && !notNeeded && !!r.makeup_date;
                      const student = studentMap[r.student_id];
                      const doneDate = completed ? r.makeup_completed_date || r.makeup_date : null;
                      const statusText = notNeeded
                        ? '보충 불필요'
                        : completed
                        ? `보충완료 · ${shortDate(doneDate)}`
                        : scheduled
                        ? `보충예정 · ${shortDate(r.makeup_date)}`
                        : '보충 미정';
                      const name = student?.name || '삭제된 학생';
                      const reasonText = absenceReasonLabel(r);
                      const info = attendanceClassInfo(r, classMaps);
                      const classLine = classInfoLine(info);
                      const tooltip = `${name} · ${reasonText}${
                        classLine ? ` · ${classLine}` : ''
                      } · ${statusText}`;

                      if (notNeeded) {
                        return (
                          <div
                            key={r.id}
                            className="flex items-start gap-1 rounded bg-background-100 px-1.5 py-1 text-[11px] leading-tight text-foreground-500"
                          >
                            <button
                              type="button"
                              onClick={() => setScheduleTarget(r)}
                              title={`${tooltip} — 클릭해서 보충 일정 잡기`}
                              className="min-w-0 flex-1 cursor-pointer text-left transition-opacity hover:opacity-80"
                            >
                              <span className="flex items-center gap-1">
                                <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
                                {info.timeLabel && (
                                  <span className="shrink-0 rounded bg-black/10 px-1 text-[9px] font-medium">
                                    {info.timeLabel}
                                  </span>
                                )}
                              </span>
                              <span className="mt-0.5 block truncate">
                                <i className="ri-bookmark-line mr-0.5 text-[10px]"></i>
                                {reasonText}
                              </span>
                              <span className="mt-0.5 block truncate text-[10px] opacity-80">
                                {statusText}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => restoreNeeded(r)}
                              disabled={saving}
                              title="보충 필요로 되돌리기"
                              className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition-colors hover:bg-secondary-100 hover:text-secondary-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <i className="ri-arrow-go-back-line text-xs"></i>
                            </button>
                          </div>
                        );
                      }

                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handleRecordClick(r)}
                          title={
                            completed
                              ? `${tooltip} — 클릭해서 보충 완료 정보 보기`
                              : `${tooltip} — 클릭해서 보충 일정 ${scheduled ? '수정' : '잡기'}`
                          }
                          className={`block w-full cursor-pointer rounded px-1.5 py-1 text-left text-[11px] leading-tight transition-all hover:ring-2 hover:ring-primary-300 ${
                            completed
                              ? 'bg-primary-100 text-primary-800'
                              : scheduled
                              ? 'bg-secondary-100 text-secondary-800'
                              : 'bg-accent-500 text-background-50'
                          }`}
                        >
                          <span className="flex items-center gap-1">
                            <span className="min-w-0 flex-1 truncate font-semibold">{name}</span>
                            {info.timeLabel && (
                              <span className="shrink-0 rounded bg-black/10 px-1 text-[9px] font-medium">
                                {info.timeLabel}
                              </span>
                            )}
                          </span>
                          <span className="mt-0.5 block truncate">
                            <i className="ri-bookmark-line mr-0.5 text-[10px]"></i>
                            {reasonText}
                          </span>
                          <span className="mt-0.5 block truncate text-[10px] opacity-80">
                            {statusText}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 보충 잡지 않은 결석 */}
      <div className="overflow-hidden rounded-lg border border-accent-200 bg-white">
        <button
          type="button"
          onClick={() => setShowUnscheduled((v) => !v)}
          className={`flex w-full cursor-pointer flex-col gap-1 bg-accent-50 px-4 py-3 text-left sm:flex-row sm:items-center sm:justify-between ${
            showUnscheduled ? 'border-b border-accent-200' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-error-warning-line text-lg"></i>
            </span>
            <div>
              <span className="text-sm font-bold text-foreground-950">
                보충 잡지 않은 결석 ({month + 1}월 · {activeTeacherName})
              </span>
              <span className="ml-2 text-xs text-foreground-500">
                이 달에 보충 일정을 정하지 못한 결석이에요
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
              {filteredUnscheduled.length}건
            </span>
            <i
              className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${
                showUnscheduled ? 'rotate-180' : ''
              }`}
            ></i>
          </div>
        </button>

        {showUnscheduled && (filteredUnscheduled.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-check-double-line text-2xl"></i>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground-700">
              보충 일정이 안 잡힌 결석이 없어요.
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              모든 결석에 보충 일정이 잡혔거나, 보충이 완료·불필요로 처리됐어요.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {filteredUnscheduled.map((r) => {
              const student = studentMap[r.student_id];
              return (
                <div
                  key={r.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-sm font-bold text-accent-700">
                      {student?.name.charAt(0) || '?'}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {student?.name || '삭제된 학생'}
                        </span>
                        {student?.grade && (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                            {student.grade}
                          </span>
                        )}
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                          보충 미정
                        </span>
                      </div>
                      <span className="text-xs text-foreground-500">
                        결석일 {displayDate(r.date)} · {absenceReasonLabel(r)}
                        {classInfoLine(attendanceClassInfo(r, classMaps))
                          ? ` · ${classInfoLine(attendanceClassInfo(r, classMaps))}`
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <button
                      onClick={() => markNotNeeded(r)}
                      disabled={saving}
                      title="이 결석은 보충이 필요 없어요"
                      className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className="ri-forbid-line"></i> 보충 불필요
                    </button>
                    <button
                      onClick={() => setScheduleTarget(r)}
                      className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                    >
                      <i className="ri-calendar-schedule-line"></i> 보충 일정 잡기
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* 보충 불필요로 처리한 결석 (되돌리기) */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <button
          type="button"
          onClick={() => setShowNotNeeded((v) => !v)}
          className={`flex w-full cursor-pointer flex-col gap-1 bg-background-50 px-4 py-3 text-left sm:flex-row sm:items-center sm:justify-between ${
            showNotNeeded ? 'border-b border-background-200' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-foreground-600">
              <i className="ri-forbid-line text-lg"></i>
            </span>
            <div>
              <span className="text-sm font-bold text-foreground-950">
                보충 불필요로 처리한 결석 ({month + 1}월 · {activeTeacherName})
              </span>
              <span className="ml-2 text-xs text-foreground-500">
                잘못 처리했다면 다시 ‘보충 필요’로 되돌릴 수 있어요
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="rounded-full bg-background-200 px-2.5 py-1 text-xs font-bold text-foreground-600">
              {filteredNotNeeded.length}건
            </span>
            <i
              className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${
                showNotNeeded ? 'rotate-180' : ''
              }`}
            ></i>
          </div>
        </button>

        {showNotNeeded && (filteredNotNeeded.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-forbid-2-line text-2xl"></i>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground-700">
              보충 불필요로 처리한 결석이 없어요.
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              보충이 필요 없는 결석을 처리하면 이곳에 쌓이고, 언제든 다시 되돌릴 수 있어요.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {filteredNotNeeded.map((r) => {
              const student = studentMap[r.student_id];
              return (
                <div
                  key={r.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-sm font-bold text-foreground-600">
                      {student?.name.charAt(0) || '?'}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {student?.name || '삭제된 학생'}
                        </span>
                        {student?.grade && (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                            {student.grade}
                          </span>
                        )}
                        <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-500">
                          보충 불필요
                        </span>
                      </div>
                      <span className="text-xs text-foreground-500">
                        결석일 {displayDate(r.date)} · {absenceReasonLabel(r)}
                        {classInfoLine(attendanceClassInfo(r, classMaps))
                          ? ` · ${classInfoLine(attendanceClassInfo(r, classMaps))}`
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <button
                      onClick={() => restoreNeeded(r)}
                      disabled={saving}
                      title="이 결석을 다시 보충이 필요한 상태로 되돌려요"
                      className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className="ri-arrow-go-back-line"></i> 보충 필요로 되돌리기
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <MakeupScheduleModal
        open={!!scheduleTarget}
        studentName={scheduleTarget ? studentMap[scheduleTarget.student_id]?.name || '학생' : ''}
        absenceDate={scheduleTarget ? displayDate(scheduleTarget.date) : ''}
        initialDate={scheduleTarget?.makeup_date || null}
        initialTime={scheduleTarget?.makeup_time || null}
        onClose={() => setScheduleTarget(null)}
        onSave={scheduleMakeup}
        makeupStatus={scheduleTarget?.makeup_status || null}
        onMarkNotNeeded={scheduleTarget ? () => markNotNeeded(scheduleTarget) : undefined}
        onRestoreNeeded={scheduleTarget ? () => restoreNeeded(scheduleTarget) : undefined}
      />

      <MakeupCompletedModal
        open={!!completedTarget}
        studentName={completedTarget ? studentMap[completedTarget.student_id]?.name || '학생' : ''}
        studentGrade={
          completedTarget ? studentMap[completedTarget.student_id]?.grade || null : null
        }
        absenceDate={completedTarget ? displayDate(completedTarget.date) : ''}
        reason={completedTarget ? absenceReasonLabel(completedTarget) : ''}
        classInfo={
          completedTarget ? classInfoLine(attendanceClassInfo(completedTarget, classMaps)) : ''
        }
        completedDate={
          completedTarget
            ? displayDate(
                completedTarget.makeup_completed_date ||
                  completedTarget.makeup_date ||
                  completedTarget.date
              )
            : ''
        }
        completedTime={completedTarget ? formatTime(completedTarget.makeup_time) || '-' : '-'}
        onClose={() => setCompletedTarget(null)}
        onEdit={
          completedTarget
            ? () => {
                const target = completedTarget;
                setCompletedTarget(null);
                setScheduleTarget(target);
              }
            : undefined
        }
      />
    </div>
  );
}